import { BaseElement, Descendant } from 'slate';

import type { Theme } from '@myn/mui';

import { getBoxBorderWithHover, getValueTextColor } from './helpers.js';

// Define custom elements
export type CustomElement = BaseElement & {
  type:
    | 'block-quote'
    | 'bulleted-list'
    | 'list-item'
    | 'numbered-list'
    | 'link'
    | 'heading-one'
    | 'heading-two'
    | 'heading-three'
    | 'heading-four'
    | 'heading-five'
    | 'heading-six'
    | 'paragraph'
    | 'title'
    | 'placeholder';
  url?: string;
  children: Descendant[];
};

type ElementAttributes = {
  type: CustomElement['type'];
  url?: string;
};

type TextAttributes = {
  code?: boolean;
  strikethrough?: boolean;
  italic?: boolean;
  bold?: boolean;
  underline?: boolean;
};

export const ELEMENT_TAGS: Record<
  string,
  (el: HTMLElement) => ElementAttributes
> = {
  A: (el) => ({ type: 'link', url: el.getAttribute('href')! }),
  BLOCKQUOTE: () => ({ type: 'block-quote' }),
  H1: () => ({ type: 'heading-one' }),
  H2: () => ({ type: 'heading-two' }),
  H3: () => ({ type: 'heading-three' }),
  H4: () => ({ type: 'heading-four' }),
  H5: () => ({ type: 'heading-five' }),
  H6: () => ({ type: 'heading-six' }),
  LI: () => ({ type: 'list-item' }),
  OL: () => ({ type: 'numbered-list' }),
  P: () => ({ type: 'paragraph' }),
  UL: () => ({ type: 'bulleted-list' }),
};

export const TEXT_TAGS: Record<string, () => TextAttributes> = {
  CODE: () => ({ code: true }),
  DEL: () => ({ strikethrough: true }),
  EM: () => ({ italic: true }),
  I: () => ({ italic: true }),
  S: () => ({ strikethrough: true }),
  STRONG: () => ({ bold: true }),
  U: () => ({ underline: true }),
};

type getEditorStylesProps = {
  theme: Theme;
  isDisabled: boolean;
  isReadOnlyAccess: boolean;
  isFocused: boolean;
  isError?: boolean;
};

export const getEditorStyles = ({
  theme,
  isDisabled,
  isReadOnlyAccess,
  isFocused,
  isError,
}: getEditorStylesProps) => {
  return {
    overflowY: 'auto',
    '&:hover': isError
      ? {}
      : {
          border: getBoxBorderWithHover(
            isFocused,
            isDisabled,
            isReadOnlyAccess,
            theme,
          ),
        },
    'span[data-slate-node="text"]': {
      display: 'inline-block',
      width: '100%',
    },
    '[data-slate-editor] > * + *': {
      marginTop: '1em',
    },

    'li, p': {
      fontWeight: 300,
    },
    strong: {
      fontWeight: 600,
    },

    "& > [role='textbox']": {
      height: '100%',
    },
    "& > [role='textbox']:focus-visible": {
      outline: 'none',
    },
    em: {
      fontStyle: 'italic',
    },
    ol: {
      margin: '0px',
    },
    ul: {
      margin: '0px',
    },
    'li:not(:last-child)': {
      marginBottom: '5px',
    },

    '[data-slate-placeholder="true"]': {
      color: `${theme.palette.text.secondary} !important`,
      fontSize: `0.875rem !important`,
      fontWeight: `400 !important`,
    },
    '[data-slate-node="value"]': {
      color: getValueTextColor(isDisabled, isReadOnlyAccess, theme),
    },
  };
};
